import { useEffect, useState } from 'react';
import { getAllClasses, createClass, updateClass } from '../../../api/training';
import { toast } from 'react-toastify';
import ClassesHeader from './components/ClassesHeader';
import ClassesTable from './components/ClassesTable';
import ClassesModal from './components/ClassesModal';

const Classes = () => {
  const initialClass = {
    description: '',
    faculty_id: '',
    technology_id: '',
    start_date: '',
    end_date: '',
    min_enrollment: '',
    max_enrollment: '',
    status: 'ENROLLMENT',
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClassId, setEditingClassId] = useState(null);
  const [currentClass, setCurrentClass] = useState(initialClass);
  const [classesList, setClassesList] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [accessDenied, setAccessDenied] = useState(false);

  const openModalForNewClass = () => {
    setIsEditMode(false);
    setEditingClassId(null);
    setCurrentClass(initialClass);
    setIsModalOpen(true);
  };

  const openModalForUpdate = (classId) => {
    const classData = classesList.find(
      (c) => c.class_id === classId || c.id === classId
    );
    if (!classData) {
      toast.error('Class not found');
      return;
    }

    console.log('Edit class - Full classData:', classData);

    // Format dates for the date input (YYYY-MM-DD)
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    };

    setIsEditMode(true);
    setEditingClassId(classData.class_id || classData.id);
    setCurrentClass({
      description: classData.description || '',
      faculty_id: classData.faculty_id || '',
      technology_id: classData.technology_id || '',
      start_date: formatDateForInput(classData.start_date),
      end_date: formatDateForInput(classData.end_date),
      min_enrollment: classData.min_enrollment || '',
      max_enrollment: classData.max_enrollment || '',
      status: classData.status || 'ENROLLMENT',
      // Display-only fields (not editable)
      created: classData.created || '',
      created_by: classData.created_by || '',
      updated: classData.updated || '',
      updated_by: classData.updated_by || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingClassId(null);
    setCurrentClass(initialClass);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      console.log('Form data received:', formData);

      // Helper to parse integer safely
      const toInteger = (value) => {
        if (!value && value !== 0) return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
      };

      // Helper to convert date to MM/DD/YYYY format
      const toMMDDYYYY = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };

      // Build class data matching API schema (camelCase field names)
      const classData = {
        description: formData.description?.trim(),
        status: formData.status,
      };

      // Add optional fields only if they have values
      const facultyId = toInteger(formData.faculty_id);
      if (facultyId !== null) classData.facultyId = facultyId;

      const technologyId = toInteger(formData.technology_id);
      if (technologyId !== null) classData.technologyId = technologyId;

      const startDate = toMMDDYYYY(formData.start_date);
      if (startDate) classData.startDate = startDate;

      const endDate = toMMDDYYYY(formData.end_date);
      if (endDate) classData.endDate = endDate;

      const minEnrollment = toInteger(formData.min_enrollment);
      if (minEnrollment !== null) classData.minEnrollment = minEnrollment;

      const maxEnrollment = toInteger(formData.max_enrollment);
      if (maxEnrollment !== null) classData.maxEnrollment = maxEnrollment;

      console.log('Data being sent to API:', classData);

      if (isEditMode) {
        await updateClass(editingClassId, classData);
        toast.success('Class Updated Successfully');
      } else {
        await createClass(classData);
        toast.success('Class Created Successfully');
      }

      closeModal();
      getData();
    } catch (error) {
      console.error('Error saving class:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        toast.error('Access denied: You do not have permission to manage classes');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 400) {
        toast.error(errorMessage || 'Invalid data. Please check your input');
      } else {
        toast.error(errorMessage || 'Failed to save class');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getData = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const response = await getAllClasses(1, 100);
      console.log('Classes API response:', response);

      if (response && response.data) {
        const data = response.data;
        // Handle structure: { message, data: { totalRecords, pageNum, pageSize, records } }
        const records = data.data?.records || data.records || [];
        console.log('Classes list extracted:', records);
        setClassesList(Array.isArray(records) ? records : []);
      } else if (Array.isArray(response)) {
        setClassesList(response);
      } else {
        setClassesList([]);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      // Check for 403 Forbidden - show on UI instead of toast
      if (statusCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
        setAccessDenied(true);
      } else if (error.message === 'Network Error' || !error.response) {
        setAccessDenied(true);
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error(errorMessage || 'Failed to load classes');
      }
      setClassesList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter classes based on search term
  const filteredClasses = classesList.filter((classItem) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      classItem.description?.toLowerCase().includes(searchLower) ||
      classItem.faculty_name?.toLowerCase().includes(searchLower) ||
      classItem.technology_name?.toLowerCase().includes(searchLower) ||
      classItem.status?.toLowerCase().includes(searchLower) ||
      String(classItem.class_id || '').includes(searchLower)
    );
  });

  // Show access denied screen
  if (accessDenied) {
    return (
      <div className="p-5">
        <div className="border border-gray-300 rounded-lg">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
            <p className="text-gray-500 text-center max-w-md">
              You are not authorized to manage classes. Please contact your Admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Header with actions */}
      <div className="border border-gray-300 rounded-lg relative">
        <ClassesHeader
          onReload={getData}
          onAddClass={openModalForNewClass}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <ClassesTable
            data={filteredClasses}
            onEditClass={openModalForUpdate}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Classes Modal */}
      <ClassesModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentClass}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Classes;
